/**
 * Shared helpers for the admin → user WhatsApp deep links.
 *
 * Every "send credentials" flow (new user created, welcome message resent,
 * password reset) opens `wa.me` with a pre-filled message in the admin's own
 * WhatsApp — nothing is sent automatically, the admin still presses send.
 *
 * The onboarding links live here so a change (new video, new store listing)
 * lands in every message at once instead of drifting between modals.
 */

export const APP_STORE_LINK =
  "https://apps.apple.com/us/app/%D9%88%D8%B5%D9%88%D9%84-wosool/id6756604641";

export const PLAY_STORE_LINK =
  "https://play.google.com/store/apps/details?id=com.wosool.wosoolapp";

// Unlisted intro video shown to parents only, walking them through login,
// live tracking, and boarding notifications before their first use.
export const PARENT_INTRO_VIDEO_LINK = "https://www.youtube.com/shorts/pn44o0Ky4yQ";

/** The two country codes a Palestinian mobile line can be reachable on. */
export const PALESTINE_COUNTRY_CODES = ["970", "972"];

/** Strip "+", spaces, dashes and brackets — leaves bare digits. */
function toDigits(phone) {
  return (phone || "").replace(/[^\d]/g, "");
}

/**
 * Reduce a number to its national part: no country code, no trunk "0".
 * "+970561120001" / "970561120001" / "0561120001" all → "561120001".
 *
 * @param {string} phone
 * @returns {string}
 */
export function toNationalNumber(phone) {
  let digits = toDigits(phone);

  const code = PALESTINE_COUNTRY_CODES.find((c) => digits.startsWith(c));
  if (code) {
    digits = digits.substring(code.length);
  }

  if (digits.startsWith("0")) {
    digits = digits.substring(1);
  }

  return digits;
}

/**
 * The country code already baked into a stored number, if any.
 *
 * @param {string} phone
 * @returns {string | null} "970", "972", or null for local-format numbers
 */
export function detectCountryCode(phone) {
  const digits = toDigits(phone);
  return PALESTINE_COUNTRY_CODES.find((c) => digits.startsWith(c)) || null;
}

/**
 * Whether to let the admin pick the prefix before opening WhatsApp.
 *
 * True for any Palestinian-context mobile — both the ambiguous local form
 * ("056…"/"059…", reachable on either code) and numbers already stored as
 * "+970…"/"+972…", because those were saved from whatever the admin typed at
 * signup and are regularly wrong. Foreign numbers are left alone.
 *
 * @param {string} phone
 * @returns {boolean}
 */
export function needsCountryCodeSelection(phone) {
  const digits = toDigits(phone);
  if (!digits) return false;
  if (detectCountryCode(phone)) return true;
  // Local form: 059xxxxxxx / 0568xxxxx, or the same without the trunk zero.
  return /^0?5\d{7,8}$/.test(digits);
}

/**
 * Normalize a stored phone number into the bare digits wa.me expects.
 *
 * When `countryCode` is given it **wins** — any existing 970/972 prefix and
 * trunk "0" are replaced, so picking +972 on a number stored as "+970…"
 * actually sends to +972. (Previously the code was only substituted for a
 * leading "0", so the admin's choice was silently dropped for numbers already
 * in international form.)
 *
 * @param {string} phone - As stored (may be "+970…", "059…", spaced)
 * @param {string} [countryCode] - "970" / "972"; empty means "leave as-is"
 * @returns {string}
 */
export function formatPhoneForWhatsApp(phone, countryCode = "") {
  if (countryCode) {
    return countryCode + toNationalNumber(phone);
  }
  return toDigits(phone);
}

/**
 * The exact number a given choice would dial — for showing the admin what
 * they're about to send to.
 *
 * @param {string} phone
 * @param {string} [countryCode]
 * @returns {string} e.g. "+970561120001"
 */
export function previewWhatsAppNumber(phone, countryCode = "") {
  const formatted = formatPhoneForWhatsApp(phone, countryCode);
  return formatted ? `+${formatted}` : "";
}

/**
 * Open WhatsApp in a new tab with the message pre-filled.
 *
 * @param {string} phone
 * @param {string} message - Plain text; encoded here
 * @param {string} [countryCode]
 */
export function openWhatsApp(phone, message, countryCode = "") {
  const formattedPhone = formatPhoneForWhatsApp(phone, countryCode);
  const encodedMessage = encodeURIComponent(message);

  window.open(
    formattedPhone
      ? `https://wa.me/${formattedPhone}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`,
    "_blank"
  );
}

/**
 * Build the account-created credentials message (the signup welcome text).
 *
 * ONE shared builder for every "share the password" surface — the same
 * i18n templates were previously re-interpolated inline by each modal.
 * Pass `kindergartenName` explicitly: a root admin's own
 * `kindergartenSettings` is null, so callers must resolve the school name
 * of the user being messaged themselves.
 *
 * @param {Function} t - i18next translate function
 * @param {{ kindergartenName: string, phone: string, password: string,
 *          isParent?: boolean }} params - parents get the intro video link.
 * @returns {string} plain-text message for openWhatsApp / clipboard
 */
export function buildParentCredentialsMessage(
  t,
  { kindergartenName, phone, password, isParent = true }
) {
  return t(
    isParent
      ? "children.parentWithChildren.successModal.whatsappMessageParent"
      : "children.parentWithChildren.successModal.whatsappMessage",
    {
      kindergartenName: kindergartenName || "Wosool",
      phone,
      password,
      appStoreLink: APP_STORE_LINK,
      playStoreLink: PLAY_STORE_LINK,
      videoLink: PARENT_INTRO_VIDEO_LINK,
    }
  );
}

/**
 * Build the credential-free welcome message — the one that points at a claim
 * link instead of spelling the password out.
 *
 * This is the shape the WhatsApp Business API template will use: Meta rejects a
 * UTILITY template carrying a credential (only AUTHENTICATION templates may
 * carry a passcode, and those are gated behind Business Verification and a
 * 2,000/day tier). Using the same wording for the manual send keeps the two
 * paths saying the same thing, and means the parent-facing copy is already
 * proven by the time the API send lands.
 *
 * A teacher gets a teacher's text — what the app is for them, as the app's own
 * hand-off says it (`msg_edu_admin_teacher_share`) — never the parents' bus.
 * A rider who tracks their own bus gets theirs: their own bus, not their
 * children's — the words BusWay's welcome SMS uses for a rider («تابعوا باصكم»).
 *
 * @param {Function} t - i18next translate function
 * @param {{ kindergartenName: string, name: string, claimUrl: string, role?: string,
 *          selfRider?: boolean }} params
 * @returns {string} plain-text message for openWhatsApp / clipboard
 */
export function buildWelcomeClaimMessage(t, { kindergartenName, name, claimUrl, role, selfRider = false }) {
  const key =
    role === "teacher"
      ? "users.welcomeMessage.claimMessageTeacher"
      : selfRider
      ? "users.welcomeMessage.claimMessageRider"
      : "users.welcomeMessage.claimMessage";
  return t(key, {
    kindergartenName: kindergartenName || "Wosool",
    name,
    claimUrl,
    appLink: `${window.location.origin}/app-link`,
  });
}

/**
 * The credentials message for a new rider's account — the one time its
 * password exists in the clear (`POST /rider/` answers it once).
 *
 * Not the parents' creation text, which tells a family their *child's*
 * account was made: a rider follows their own bus. Formal plural, as every
 * word BusWay sends a rider is — no rider has a gender field.
 *
 * @param {Function} t - i18next translate function
 * @param {{ kindergartenName: string, name: string, phone: string, password: string }} params
 * @returns {string} plain-text message for openWhatsApp / clipboard
 */
export function buildRiderCredentialsMessage(t, { kindergartenName, name, phone, password }) {
  return t("riders.created.whatsappMessage", {
    kindergartenName: kindergartenName || "Wosool",
    name,
    phone,
    password,
    appStoreLink: APP_STORE_LINK,
    playStoreLink: PLAY_STORE_LINK,
  });
}
